import { Database } from "lucide-react";
import type { FC } from "react";
import React from "react";
import { TableVirtuoso } from "react-virtuoso";

import { bindArgs } from "@core/foundation/function";

import { useInfiniteQuery } from "@react/foundation/query";
import { Button } from "@react/ui-primitives/button";
import { Skeleton } from "@react/ui-primitives/skeleton";
import {
  Table,
  TableBody,
  TableCard,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@react/ui-primitives/table";

import { PageView } from "../../../shared/components/PageView.tsx";
import { useQueries } from "../../../shared/hooks/useQueries.ts";
import { useTablesActions } from "../hooks/useTablesActions.ts";

import { TableItem } from "./TableItem.tsx";

const COLUMNS = 2;

export const TablesView: FC = () => {
  const { syncAllTables } = useTablesActions();

  const { getTablesQueryOptions } = useQueries();
  const tablesQueryOptions = getTablesQueryOptions({});
  const tables = useInfiniteQuery(tablesQueryOptions.infinite);

  return (
    <div className="mx-auto w-full">
      <PageView
        actions={
          <div className="flex gap-2">
            <Button
              disabled={syncAllTables.isPending}
              onClick={bindArgs(syncAllTables.mutate, undefined)}
              size="sm"
              variant="outline"
            >
              Sync All
            </Button>
          </div>
        }
        icon={<Database />}
        subtitle={`${tables.items.length} table(s)`}
        title="DynamoDB Tables"
      >
        <TableCard>
          {tables.status === "pending" && (
            <Table className="table-fixed">
              <TableHeader>
                <TableRow variant="header">
                  <TableHead className="w-[70%]">Table</TableHead>
                  <TableHead className="w-[30%] text-right">
                    Last synced
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 3 }, (_1, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: COLUMNS }, (_2, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text-line" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {tables.status === "error" && (
            <Table className="table-fixed">
              <TableHeader>
                <TableRow variant="header">
                  <TableHead className="w-[70%]">Table</TableHead>
                  <TableHead className="w-[30%] text-right">
                    Last synced
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={COLUMNS} variant="error">
                    Failed to load tables.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
          {tables.status === "success" && tables.items.length === 0 && (
            <Table className="table-fixed">
              <TableHeader>
                <TableRow variant="header">
                  <TableHead className="w-[70%]">Table</TableHead>
                  <TableHead className="w-[30%] text-right">
                    Last synced
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={COLUMNS} variant="info">
                    No tables found.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
          {tables.status === "success" && tables.items.length > 0 && (
            <TableVirtuoso
              components={{
                Table: props => <Table className="table-fixed" {...props} />,
                TableBody,
                TableHead: TableHeader,
                TableRow,
              }}
              computeItemKey={(_index, tableName) => tableName}
              data={tables.items}
              endReached={tables.loadNextPage}
              fixedHeaderContent={() => (
                <TableRow variant="header">
                  <TableHead className="w-[70%]">Table</TableHead>
                  <TableHead className="w-[30%] text-right">
                    Last synced
                  </TableHead>
                </TableRow>
              )}
              itemContent={(_index, tableName) => (
                <TableItem tableName={tableName} />
              )}
              useWindowScroll
            />
          )}
        </TableCard>
      </PageView>
    </div>
  );
};
